import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "boids-flock";

const DEFAULT_BG = "#0a1422";
const DEFAULT_BOID_A = "#67e8f9";
const DEFAULT_BOID_B = "#f0abfc";

// Classic Reynolds boids parameters. The three rules:
//   - separation: steer to avoid crowding nearby flockmates
//   - alignment:  steer toward the average heading of nearby flockmates
//   - cohesion:   steer toward the average position of nearby flockmates
// Tweaking the weights changes whether the swarm clumps tight, drifts loose,
// or spirals.
const BOID_DENSITY = 0.00006;     // boids per pixel² of canvas
const MIN_BOIDS = 30;
const MAX_BOIDS = 140;
const NEIGHBOR_RADIUS = 60;       // px — vision cone radius
const SEPARATION_RADIUS = 22;     // px — personal-space radius
const MAX_SPEED = 1.8;            // px/frame
const MAX_FORCE = 0.05;           // px/frame² — steering strength cap
const W_SEP = 1.6;
const W_ALI = 1.0;
const W_COH = 0.85;
const TRAIL_FADE = 0.10;          // how strongly the previous frame fades each tick

function rand(min, max) { return min + Math.random() * (max - min); }

// Limit a 2D vector's magnitude to `max`. Returns the same object for chaining.
function limit(v, max) {
  const m = Math.sqrt(v.x * v.x + v.y * v.y);
  if (m > max && m > 0) {
    v.x = (v.x / m) * max;
    v.y = (v.y / m) * max;
  }
  return v;
}

export default function BoidsFlock({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_BOID_A, DEFAULT_BOID_B]);
  const boidsRef = useRef([]);
  const primedRef = useRef(false);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_BOID_A),
      pickColor(colors, 2, DEFAULT_BOID_B),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const seedBoids = () => {
      const target = Math.round(width * height * BOID_DENSITY);
      const count = Math.max(MIN_BOIDS, Math.min(MAX_BOIDS, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        const a = rand(0, Math.PI * 2);
        arr[i] = {
          x: rand(0, width),
          y: rand(0, height),
          // Random initial heading at full speed, so the flock isn't "born"
          // moving in one direction.
          vx: Math.cos(a) * MAX_SPEED,
          vy: Math.sin(a) * MAX_SPEED,
          // Two sub-flocks share the same rules but render in different
          // colors → visually you see them mix and weave through each
          // other without any kind of segregation logic.
          slot: Math.random() < 0.5 ? 1 : 2,
        };
      }
      boidsRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedBoids();
      primedRef.current = false;
    };

    const tick = () => {
      const [bg, boidA, boidB] = colorsRef.current;
      const boids = boidsRef.current;
      const n = boids.length;

      // Translucent overlay → produces faint motion trails behind each boid
      // for free, without storing per-boid trail buffers.
      if (!primedRef.current) {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, width, height);
        primedRef.current = true;
      } else {
        ctx.fillStyle = rgba(bg, TRAIL_FADE);
        ctx.fillRect(0, 0, width, height);
      }

      // O(n²) neighbor scan — fine up to ~150 boids on canvas. For each
      // boid we accumulate three steering vectors based on neighbors.
      for (let i = 0; i < n; i += 1) {
        const b = boids[i];

        let sepX = 0, sepY = 0;
        let aliX = 0, aliY = 0;
        let cohX = 0, cohY = 0;
        let neighborCount = 0;
        let separationCount = 0;

        for (let j = 0; j < n; j += 1) {
          if (i === j) continue;
          const o = boids[j];
          const dx = o.x - b.x;
          const dy = o.y - b.y;
          const distSq = dx * dx + dy * dy;
          if (distSq > NEIGHBOR_RADIUS * NEIGHBOR_RADIUS) continue;
          const dist = Math.sqrt(distSq) || 0.0001;

          // Separation: push away from neighbors that are too close, with
          // strength inversely proportional to distance.
          if (dist < SEPARATION_RADIUS) {
            sepX -= dx / dist;
            sepY -= dy / dist;
            separationCount += 1;
          }

          // Alignment: average velocity of the neighborhood.
          aliX += o.vx;
          aliY += o.vy;

          // Cohesion: average position of the neighborhood.
          cohX += o.x;
          cohY += o.y;

          neighborCount += 1;
        }

        // Convert each accumulator into a steering force pointing from
        // current velocity toward the desired velocity, then clamp.
        let ax = 0, ay = 0;

        if (separationCount > 0) {
          sepX /= separationCount;
          sepY /= separationCount;
          const m = Math.sqrt(sepX * sepX + sepY * sepY) || 0.0001;
          sepX = (sepX / m) * MAX_SPEED - b.vx;
          sepY = (sepY / m) * MAX_SPEED - b.vy;
          const sep = limit({ x: sepX, y: sepY }, MAX_FORCE);
          ax += sep.x * W_SEP;
          ay += sep.y * W_SEP;
        }

        if (neighborCount > 0) {
          aliX /= neighborCount;
          aliY /= neighborCount;
          const m = Math.sqrt(aliX * aliX + aliY * aliY) || 0.0001;
          aliX = (aliX / m) * MAX_SPEED - b.vx;
          aliY = (aliY / m) * MAX_SPEED - b.vy;
          const ali = limit({ x: aliX, y: aliY }, MAX_FORCE);
          ax += ali.x * W_ALI;
          ay += ali.y * W_ALI;

          cohX = cohX / neighborCount - b.x;
          cohY = cohY / neighborCount - b.y;
          const m2 = Math.sqrt(cohX * cohX + cohY * cohY) || 0.0001;
          cohX = (cohX / m2) * MAX_SPEED - b.vx;
          cohY = (cohY / m2) * MAX_SPEED - b.vy;
          const coh = limit({ x: cohX, y: cohY }, MAX_FORCE);
          ax += coh.x * W_COH;
          ay += coh.y * W_COH;
        }

        b.vx += ax;
        b.vy += ay;
        const speed = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
        if (speed > MAX_SPEED) {
          b.vx = (b.vx / speed) * MAX_SPEED;
          b.vy = (b.vy / speed) * MAX_SPEED;
        }

        b.x += b.vx;
        b.y += b.vy;

        // Toroidal wrap so the flock can keep flowing forever.
        if (b.x < 0) b.x = width;
        if (b.x > width) b.x = 0;
        if (b.y < 0) b.y = height;
        if (b.y > height) b.y = 0;
      }

      // Render: small triangle pointing along velocity. Triangles are
      // unfilled outlines — keeps the swarm legible against the trails.
      for (let i = 0; i < n; i += 1) {
        const b = boids[i];
        const angle = Math.atan2(b.vy, b.vx);
        const color = b.slot === 1 ? boidA : boidB;
        ctx.fillStyle = rgba(color, 0.95);
        ctx.beginPath();
        // Triangle local coords: tip forward (+x), two tails behind.
        const tipX = b.x + Math.cos(angle) * 6;
        const tipY = b.y + Math.sin(angle) * 6;
        const leftX = b.x + Math.cos(angle + 2.5) * 4;
        const leftY = b.y + Math.sin(angle + 2.5) * 4;
        const rightX = b.x + Math.cos(angle - 2.5) * 4;
        const rightY = b.y + Math.sin(angle - 2.5) * 4;
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(leftX, leftY);
        ctx.lineTo(rightX, rightY);
        ctx.closePath();
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
